<template>
  <div id="heartbeat">

    <div class="group">
      <div class="label-with-aligned-content">
        <label>{{ $t('heartbeat.select_columns') }}</label>
        <div>
          <button class="btn" v-for="(p, i) of presets" @click="applyPreset(p)" :key="`pr_${i}`">{{p.label}}</button>
        </div>
      </div>
      <MultiSelect :options="fields" :selected="selectedFields" trackByAttribute="field"
                   @changed="selectedFields = $event"/>
    </div>

    <div class="group">
      <label>{{ $t('heartbeat.branch_filter') }}</label>
      <MultiSelect :options="branchFilter.all" :selected="branchFilter.selected" trackByAttribute="id"
                   @changed="branchFilter.selected = $event" />
    </div>

    <div class="flex">
      <div class="group" style="flex-grow: 1">
        <label>{{ $t('search') }}</label>
        <input type="text" v-model="inputSearch">
      </div>
      <div style="flex-grow: 2"></div>
      <div style="align-self: flex-end; margin-bottom: 15px">
        <button class="btn btn-primary" @click="generate">{{ $t('generate') }}</button>
      </div>
    </div>

    <div v-if="rows" class="power-table-header">
      <div v-if="search"><i>Search</i> <b> "{{search}}"</b></div>
      <div v-if="nextPingCountdownInSeconds !== null">
        {{ $t('heartbeat.next_refresh') }}: {{ nextPingCountdownInSeconds }} s
      </div>
    </div>
    <div v-if="rows && !rows.length" style="text-align: center">
      <i>No results</i>
    </div>
    <table v-else-if="rows && rows.length" class="power-table">
      <tr>
        <th><!-- Actions Column --></th>
        <th v-for="f of columns" :key="`th_${f.field}`">{{ f.label || $t(f.trans) }}</th>
      </tr>
      <tr v-if="hasColumnFilters">
        <td><!-- Actions Column --></td>
        <td v-for="(field, ci) of columns">
          <input v-if="field.filter === 'text'" type="text" v-model="localFilters[columnIndexToKey(ci)]">
          <select v-else-if="field.filter === 'bool'" v-model="localFilters[columnIndexToKey(ci)]">
            <option :value="undefined"></option>
            <option :value="true">{{$t('yes')}}</option>
            <option :value="false">{{$t('no')}}</option>
            <option value="null">NULL</option>
          </select>
        </td>
      </tr>
      <tr v-for="(row, ri) of filteredRows" :key="`tr_${ri}`">
        <td>
          <svgicon src="/icon/gear.svg" class="icon" height="20" width="20" @click="goToCompanySettings(row)" />
        </td>
        <template v-for="(field, ci) of columns">
          <td v-if="field.popup" @click="showJson(row, ci)">
            <svgicon src="/icon/campagnes.svg" class="icon" width="20" height="20" v-if="!!getColumnData(row, ci)" />
          </td>
          <td v-else-if="field.modal" @click="showModalByName(row, field.modal.name)">
            <div style="display: flex">
              <div style="margin-right: 5px">{{getColumnData(row, ci)}}</div>
              <svgicon v-if="!field.modal.showIcon || field.modal.showIcon(getColumnData(row, ci))"
                       src="icon/error.svg" width="20" height="20" />
            </div>
          </td>
          <td v-else-if="field.pill">
            <div class="circle" :style="{ backgroundColor: pillColor(getColumnData(row, ci), field.pill) }"></div>
            <div v-if="isDateValue(getColumnData(row, ci))">{{formatDateValue(getColumnData(row, ci))}}</div>
          </td>
          <td v-else>{{getColumnData(row, ci)}}</td>
        </template>
      </tr>
    </table>

  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import { debouncedComputedProperty } from "@/vueUtils";
  import MultiSelect from '@/components/tools/MultiSelect.vue';
  import JsonViewer from 'vue-json-viewer';
  import MappingAnomalies from "@/components/inventory/ExternalPosMappingAnomalies";
  import moment from "moment-timezone";
  import Vue from "vue";

  export default {
    name: "Heartbeat",

    components: { MultiSelect, JsonViewer, MappingAnomalies },

    data() {
      return {
        /* Current table filters */
        localFilters: {},

        /* Current request data */
        search: null,
        filters: {},
        columns: [],
        rows: null,

        /* Build request data (inputs) */
        inputSearch: null,
        selectedFields: [],
        branchFilter: {
          all: [],
          selected: []
        },

        /* Ping parameters */
        clockIntervalId: null,
        pingIntervalId: null,
        currentTimestamp: null,
        lastRequestTimestamp: null,
        pingPeriodInSeconds: 15,

        presets: [{
          label: 'XPOS',
          fields: ['branch.name', 'name', 'activePOS', 'posConfig.usingXPos', 'lastXposActivityAt']
        }, {
          label: 'POS Anomalies',
          fields: ['branch.name', 'name', 'inventory', 'nbPosAnomalies']
        }],

        /* Configurations, ordered alphabetically (almost) */
        fields: [{
          label: 'ID', field: 'id', filter: 'text'
        },{
          label: 'Branch canonical', field: 'branch.nameCanonical', filter: 'text',
        },{
          label: 'Branch name', field: 'branch.name', filter: 'text',
          initiallyShown: true
        },{
          label: 'Branch active', field: 'branch.active', filter: 'bool',
          pill: 'normal'
        },{
          label: 'Company canonical', field: 'nameCanonical', filter: 'text',
        },{
          label: 'Company name', field: 'name', filter: 'text',
          initiallyShown: true
        },{
          label: 'Company active', field: 'active', filter: 'bool',
          pill: 'normal'
        },{
          label: 'Backend version', field: 'compiledBackendVersion', filter: 'text',
          initiallyShown: true,
          continuouslyPing: true
        },{
          label: 'Agent build', field: 'agentBuildVersion', filter: 'text',
          /* not shown by default: the field only exists on servers running the API from 2026-11 (NA0 first) */
          continuouslyPing: true
        },{
          label: 'Closed (POS offline)', field: 'emergencyCloseExternalOffline', filter: 'bool', pill: 'inverse',
          initiallyShown: true,
          continuouslyPing: true,
        },{
          label: 'Closed (Manually)', field: 'emergencyCloseManually', filter: 'bool', pill: 'inverse',
          initiallyShown: true,
        },{
          label: 'Last activity (reception)', field: 'lastReceptionActivityAt', filter: 'text', pill: 'normal',
          initiallyShown: true,
          continuouslyPing: true,
        },{
          label: 'Last sale (company timezone)', field: 'lastSaleAt', filter: 'text',
          continuouslyPing: true
        },{
          label: 'Origin / Affiliation', field: 'branch.affiliation', filter: 'text',
        },{
          label: 'Payment processors activated', field: 'paymentProcessors', filter: 'text',
        },{
          label: 'Payment processors data', field: 'recipientAccounts', filter: 'text',
          popup: true
        },{
          label: 'POS', field: 'activePOS', filter: 'text',
          initiallyShown: true
        },{
          label: 'POS anomalies', field: 'nbPosAnomalies', filter: 'bool',
          modal: {
            name: 'posAnomalies',
            showIcon: (v) => parseInt(v) > 0
          }
        },{
          label: 'Remote accesses', field: 'remoteAccesses', filter: 'text',
          initiallyShown: true,
          popup: true,
        },{
          label: 'Taxes group', field: 'taxGroup.uniqueName', filter: 'text',
        },{
          label: 'Timezone', field: 'timezone', filter: 'text',
        },{
          label: 'XPOS active', field: 'posConfig.usingXPos', filter: 'bool', pill: 'normal'
        },{
          label: 'XPOS last activity', field: 'lastXposActivityAt', filter: 'text', pill: 'normal',
          continuouslyPing: true,
        }]
      }
    },

    computed: {
      localFiltersDebounced: debouncedComputedProperty(500, {}),
      isContinuousPing() {
        return this.columns.filter(c => !!c.continuouslyPing).length > 0
      },
      hasColumnFilters() {
        return this.columns.filter(c => !!c.filter)
      },
      contextServer() {
        return this.$store.state.contextServer;
      },
      nextPingCountdownInSeconds() {
        return this.isContinuousPing && this.currentTimestamp && this.lastRequestTimestamp
          ? Math.max(0, this.pingPeriodInSeconds - (this.currentTimestamp - this.lastRequestTimestamp))
          : null;
      },
      allowedBranches() {
        if (!this.$store.state.loggedUser.accessibleBranches) {
          return undefined;
        }
        return this.$store.state.loggedUser.accessibleBranches
          .filter(sb => sb.server.id === this.contextServer.id)
          .map(sb => ({ id: sb.branchId, name: sb.name }));
      },
      filteredRows() {
        if (!this.rows || !this.rows.length) {
          return [];
        }
        let filtersList = Object.entries(this.localFiltersDebounced)
          .map(([field, filterValue]) => [field, typeof filterValue === 'string' ? filterValue.trim() : filterValue])
          .filter(([_, filterValue]) => !!filterValue || filterValue === false)

        if (!filtersList.length) {
          return this.rows;
        }
        let filteredRows = [];
        for (let row of this.rows) {
          let passFilters = true;
          for (let [field, filterValue] of filtersList) {
            let cellData = row[field];

            /* bool filter */
            if (typeof filterValue === 'boolean') {
              if ((typeof cellData === 'boolean' || cellData === null) && filterValue !== cellData) {
                passFilters = false; break;
              }
              if (!isNaN(parseInt(cellData)) && parseInt(cellData) > 0 !== filterValue) {
                passFilters = false; break;
              }
            }
            /* string filter */
            else if (typeof filterValue === 'string') {
              filterValue = filterValue.toLowerCase().trim().replace(/\s\s+/g, ' ');
              if (filterValue === 'null' && (cellData || cellData === false)) {
                passFilters = false; break;
              }
              if (cellData && typeof cellData === 'object') {
                cellData = JSON.stringify(cellData)
              } else if (typeof cellData === 'number') {
                cellData = cellData.toString();
              } else if (typeof cellData !== 'string') {
                cellData = '';
              }
              cellData = cellData.toLowerCase();
              if (!filterValue.split(' ').every(f => cellData.includes(f))) {
                passFilters = false; break;
              }
            }
          }
          if (passFilters) {
            filteredRows.push(row);
          }
        }
        return filteredRows;
      }
    },

    watch: {
      isContinuousPing(ping) {
        this.clearIntervals();
        if (ping) {
          this.pingIntervalId = setInterval(this.refresh, this.pingPeriodInSeconds * 1000);
          this.clockIntervalId = setInterval(() => { this.currentTimestamp = this.getNowTimestamp(); }, 1000);
        }
      },
      localFilters: {
        deep: true,
        handler(val) {
          this.localFiltersDebounced = JSON.parse(JSON.stringify(val))
        }
      },
      contextServer: {
        immediate: true,
        handler() {
          this.rows = null;
          this.columns = [];
          this.branchFilter.all = [];
          this.branchFilter.selected = [];
          this.populateBranchFilter();
        }
      }
    },

    beforeDestroy() {
      this.clearIntervals();
    },

    mounted() {
      this.selectedFields = this.fields.filter(f => !!f.initiallyShown);
      this.$store.state.pageTitleTranslationKey = "advanced_navigation.heartbeat";
      this.$store.state.pageIcon = "";
    },

    methods: {
      async refresh() {
        await this.request({
          fields: this.columns,
          filters: this.filters,
          search: this.search
        });
      },

      async generate() {
        let filters = {};
        if (this.branchFilter.selected.length) {
          filters.branch = this.branchFilter.selected.map(b => b.id)
        }
        if (!filters.branch && Array.isArray(this.allowedBranches)) {
          filters.branch = this.allowedBranches.map(b => b.id)
        }
        showSpinner();
        /* generate table */
        await this.request({
          search: this.inputSearch || undefined,
          fields: this.selectedFields,
          filters
        });

        /* init local filters */
        this.localFilters = {}
        for (let [i, f] of this.selectedFields.entries()) {
          if (f.filter) {
            let initialValue = f.filter === 'bool' ? undefined : ''
            Vue.set(this.localFilters, this.columnIndexToKey(i), initialValue)
          }
        }
        hideSpinner();
      },

      async request({ search, fields, filters }) {
        let data = {
          fields: fields.map(f => f.field),
          perPage: -1,
          search,
          ...filters,
        };
        let response = await Core.noThrowRequest('GET', '/api/power-user/companies', data);
        if (response) {
          this.search = search;
          this.filters = filters;
          this.columns = fields;
          this.rows = response.data;
          this.lastRequestTimestamp = this.getNowTimestamp();
        }
      },

      applyPreset(p) {
        this.selectedFields = this.fields.filter(f => p.fields.includes(f.field));
      },

      showJson(row, col) {
        let data = this.getColumnData(row, col);

        /* When a string, try to parse as json */
        if (typeof data === 'string') {
          try { data = JSON.parse(data); } catch (e) { }
        }

        if (typeof data === 'string') {
          toast(data, 'info')
        }
        else if (typeof data === 'object' && !!data) {
          this._showModal({
            component: JsonViewer,
            componentProps: {
              value: data,
              expanded: true,
              copyable: true,
              boxed: true,
              "expand-depth": 5
            }
          });
        }
      },

      showModalByName(row, modalName) {
        if (modalName === 'posAnomalies') {
          let inventory = parseInt(row.inventoryId);
          let company = parseInt(row.companyId);
          let branch = parseInt(row.branchId);
          if (!inventory) {
            toast('missing or invalid inventoryId in row', 'error');
            return;
          }
          this._showModal({
            component: MappingAnomalies,
            componentProps: {inventory, company, branch}
          })
        } else {
          toast(`unknown modal '${modalName}'`, 'warning')
        }
      },

      _showModal({component, componentProps}) {
        this.$store.state.modalProps = {width: 800}
        this.$store.state.modalComponent = component
        this.$store.state.modalComponentProps = componentProps
        this.$modal.show('component-modal');
      },

      valueAsNullableBool(value, isInverted = false) {
        let asBool = null;
        if (typeof value === 'boolean') {
          asBool = value;
        } else if (typeof value === 'object' && value !== null && value.date) {
          asBool = moment().tz(value.timezone).subtract(60, 'seconds').format("YYYY-MM-DD HH:mm:ss") < value.date
        }
        if (typeof asBool === 'boolean' && isInverted) {
          asBool = !asBool;
        }
        return asBool;
      },

      pillColor(value, pillType) {
        let valueAsBoolOrNull = this.valueAsNullableBool(value, pillType === 'inverse');
        if (valueAsBoolOrNull === true) {
          return '#208228';
        } else if (valueAsBoolOrNull === false) {
          return '#ce1a0d';
        } else {
          return '#c3c3c3';
        }
      },

      clearIntervals() {
        if (!!this.pingIntervalId) {
          clearInterval(this.pingIntervalId);
        }
        if (!!this.clockIntervalId) {
          clearInterval(this.clockIntervalId);
        }
      },
      getNowTimestamp() {
        return moment().unix();
      },
      columnIndexToKey(cIndex) {
        return `c${cIndex}`;
      },
      getColumnData(row, colIndex) {
        return row[this.columnIndexToKey(colIndex)]
      },
      isDateValue(value) {
        return typeof value === 'object' && value !== null && value.date
      },
      formatDateValue(value) {
        return value.date.substring(0, 19);
      },
      goToCompanySettings(row) {
        let branch = row.branchId;
        let company = row.companyId;
        this.$router.push({ path: '/company/edit', query: { branch, company } });
      },
      async populateBranchFilter() {
        if (Array.isArray(this.allowedBranches)) {
          this.branchFilter.all = this.allowedBranches.map(b => ({ id: b.id, label: b.name }));
        } else {
          let allServerBranches = await Core.request("GET", '/api/entity/branch/select', { deleted: 0 });
          this.branchFilter.all = allServerBranches.map(b => ({ id: b.id, label: b.name }));
        }
      },
    }
  }
</script>

<style lang="scss" scoped>
  .flex {
    display: flex;
  }

  .icon {
    cursor: pointer;
  }

  .circle {
    border-radius: 100%;
    $size: 30px;
    width: $size;
    height: $size;
    display: inline-block;
  }

  .json-viewer {
    button {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }

  .label-with-aligned-content {
    line-height: 14px;
    display: flex;
    justify-content: space-between;

    .btn {
      padding: 4px 12px;
      margin-bottom: 6px;
    }
  }

  .power-table-header {
    display: flex;
    justify-content: space-around;
    margin: 10px 0 20px;
  }

  .power-table {
    width: 100%;

    td,
    th {
      padding: 4px 4px;
      text-align: left;

      input, select {
        padding: 0;
        height: 24px;
        width: 100%;
      }

      &.money {
        font-family: Monospace, serif;
        text-align: right;
      }
    }

    th {
      white-space: nowrap;
      background-color: #ddd;
    }

    tr:nth-child(odd) {
      background-color: rgba(0, 0, 0, .05);
    }
  }
</style>